import JSZip from 'jszip';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { FileManagementService } from './fileManagement.service';
import { ISOXMLService } from './ISOXML/ISOXML.service';
import { UploadService } from './upload.service';

@Injectable()
export class FieldsImportService {
  fieldFromFile$: Subject<any> = new Subject<any>();

  constructor(
    private uploadService: UploadService,
    private fileManagementService: FileManagementService,
    private isoxmlService: ISOXMLService
  ) {}

  async getGeoJsonFromFieldFile(file: any) {
    const fileType = file.type;
    switch (fileType) {
      case 'application/zip':
        this.readFieldsFromZip(file);
        break;
      case 'application/xml':
      case 'text/xml':
        this.readFieldsFromXml(file).then((json) => this.fieldFromFile$.next(json));
        break;
      default:
        this.readFieldsByFileExtension(file);
    }
  }
  private async readFieldsFromZip(file: any): Promise<void> {
    const reader = new FileReader();

    reader.readAsArrayBuffer(file);

    reader.onloadend = async (e) => {
      try {
        const zip = new JSZip();
        await zip.loadAsync((e.target as any).result);
        const zippedFiles = zip.files;

        // Filtra gli shapefile, escludendo quelli di macOS
        const shpFiles = Object.keys(zippedFiles).filter(
          (key) =>
            key.toLowerCase().endsWith('.shp') &&
            !key.startsWith('__MACOSX/') &&
            !key.includes('/._')
        );

        const xmlFiles = Object.keys(zippedFiles).filter(
          (key) =>
            key.toLowerCase().endsWith('.xml') &&
            !key.startsWith('__MACOSX/') &&
            !key.includes('/._')
        );

        if (shpFiles.length > 0) {
          console.log('Trovati shapefile validi:', shpFiles);
          const formData: FormData = new FormData();
          formData.append('file', file, file.name);

          const json = await this.shapeToGeoJson(formData);
          this.fieldFromFile$.next(json);
        } else if (xmlFiles.length > 0) {
          console.log('Nessuno shapefile valido trovato, processiamo XML:', xmlFiles);
          const json = await this.readFieldsFromXml(file);
          this.fieldFromFile$.next(json);
        } else {
          console.warn('Il file ZIP non contiene né shapefile né XML validi.');
        }
      } catch (error) {
        console.error('Errore durante la lettura del file ZIP:', error);
      }
    };

    reader.onerror = (e) => {
      console.error('Errore nella lettura del file ZIP:', e);
    };
  }

  private async shapeToGeoJson(formData: FormData) {
    const response = await this.uploadService.saveFile(formData).toPromise();
    const shape = await this.fileManagementService.shapeToGeoJson(response.imageUrl).toPromise();

    const geoJsonFieldNew = shape;
    const typePoly = geoJsonFieldNew.features[0].geometry.type;
    if (typePoly === 'MultiPolygon') {
      geoJsonFieldNew.features[0].geometry.type = 'Polygon';
      const polyCoords = geoJsonFieldNew.features[0].geometry.coordinates[0];
      geoJsonFieldNew.features[0].geometry.coordinates = polyCoords;
    }
    return geoJsonFieldNew;
  }

  private async readFieldsFromXml(file: any) {
    const fields = await this.isoxmlService.ISOXMLFile2Fields(file, file.type);
    if (fields.length === 0) {
      return null;
    }
    const f = fields[0];
    return JSON.parse(f.geoJsonArea);
  }

  private readFieldsByFileExtension(file: any): void {
    const fileType = file.name.split('.').at(-1);
    if (fileType === 'kml') {
      const formData: FormData = new FormData();
      formData.append('file', file, file.name);
      this.fileManagementService
        .kmlToGeoJson(formData)
        .subscribe((res) => this.fieldFromFile$.next(res));
    }
    if (fileType === 'kmz') {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onloadend = async (e) => {
        const zip = new JSZip();
        await zip.loadAsync((e.target as any).result);
        const zippedFiles = zip.files;
        for (const key of Object.keys(zippedFiles)) {
          const content = await zippedFiles[key].async('arraybuffer');
          const zFile = new File([content], 'filename.kml');
          const formData: FormData = new FormData();
          formData.append('file', zFile, 'filename.kml');
          this.fileManagementService
            .kmlToGeoJson(formData)
            .subscribe((res) => this.fieldFromFile$.next(res));
        }
      };
    }
    if (fileType === 'shp') {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onloadend = async (e) => {
        const zip = new JSZip();
        const fileName = 'newFieldShape.zip';
        zip.file(file.name, (e.target as any).result);
        const content = await zip.generateAsync({ type: 'blob' });
        const fileObj = new File([content], fileName, {
          type: 'application/zip',
        });
        const formData: FormData = new FormData();
        formData.append('file', fileObj, fileName);
        this.shapeToGeoJson(formData).then((res) => this.fieldFromFile$.next(res));
      };
    }
  }
}
